'use client';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { BREAKPOINTS, MODEL_VERSION_SETTINGS_MAP, VIDU_WELCOME_STORAGE_KEY } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { cn, reportEvent } from '@/app/utils';
import { safeStorage } from '@/app/utils/client';
import { useLocale, useRouter } from '@/i18n/routing';
import { VIDEO_RESOURCES } from './constants';
import { PlaceholdersAndVanishInput } from './PlaceholdersAndVanishInput';
import { SequentialVideoCarousel } from './SequentialVideoCarousel';

interface NavigatorWithConnection extends Navigator {
  connection?: {
    saveData?: boolean;
  };
}

export const HeroSection = () => {
  const [currentVideoNumber, setCurrentVideoNumber] = useState(0);
  const [canLoadHeroVideo, setCanLoadHeroVideo] = useState(false);
  const [isInViewport, setIsInViewport] = useState(true);
  const sectionRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { updateFormParams, updateModelVersion, setPendingClawInput } = useStore((state) => ({
    updateFormParams: state.updateFormParams,
    updateModelVersion: state.updateModelVersion,
    setPendingClawInput: state.setPendingClawInput,
  }));
  const t = useTranslations();

  const locale = useLocale();

  const handleVideoEnded = () => {
    setCurrentVideoNumber((prev) => (prev + 1) % VIDEO_RESOURCES.length);
  };

  // 处理视频指示器选择
  function handleVideoSelect(index: number) {
    setCurrentVideoNumber(index);
  }

  // hydration 后下一帧再拉 MP4：首屏仍靠 page preload 的 poster，避免与同步渲染抢主线程
  useEffect(() => {
    const connection = (navigator as NavigatorWithConnection).connection;
    if (connection?.saveData) return;

    let cancelled = false;
    const frameId = requestAnimationFrame(() => {
      if (!cancelled) {
        setCanLoadHeroVideo(true);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, []);

  // 只监听组件滚出视口的情况
  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        setIsInViewport(entries[0].isIntersecting);
      },
      { threshold: 0.05 },
    );

    observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const initImg2VideoFormParams = (realText?: string) => {
    const currentVideoResource = VIDEO_RESOURCES[currentVideoNumber];
    const { modelVersion, settings, poster } = currentVideoResource;

    updateFormParams(
      'img2video',
      'textPrompt',
      realText || t(`home3.heroSection.prompts.${currentVideoResource.name}`),
    );
    updateFormParams('img2video', 'imagePrompts', [
      {
        id: uuidv4(),
        status: 'done',
        prompt: {
          sourceImage: poster,
          sourceImagePreview: poster,
          selectedImage: poster,
          selectedImagePreview: poster,
        },
      },
    ]);
    if (modelVersion) {
      updateFormParams('img2video', MODEL_VERSION_SETTINGS_MAP[modelVersion], settings);
      updateModelVersion('img2video', modelVersion);
    }
  };

  const handleClickTryVidu = (text?: string) => {
    const realText = text?.trim();
    if (realText) {
      safeStorage.setItem(VIDU_WELCOME_STORAGE_KEY, realText);
    }
    const currentVideoResource = VIDEO_RESOURCES[currentVideoNumber];

    reportEvent('element_click', {
      element_name: 'landing - create',
      case_name: currentVideoResource.name,
      case_index: currentVideoNumber,
    });

    // 指定了 href 的视频（如 S1 展示）直接跳转，不走生成逻辑。
    if (currentVideoResource.href) {
      router.push(currentVideoResource.href);
      return;
    }

    if (currentVideoResource.isClaw) {
      const prompt = realText || t(`home3.heroSection.prompts.${currentVideoResource.name}`) || '';
      setPendingClawInput({
        prompt,
        referenceImageUrl: currentVideoResource.clawWithReferenceImage ? currentVideoResource.poster : undefined,
      });
      router.push('/claw');
      return;
    }

    initImg2VideoFormParams(realText);
    router.push('/create/img2video');
  };

  return (
    <div
      ref={sectionRef}
      className="relative z-10 flex h-[100svh] min-h-[660px] flex-col items-center justify-center sm:justify-end md:justify-end"
    >
      {/* 视频容器 */}
      <div className="absolute inset-0 h-full w-full overflow-hidden">
        <SequentialVideoCarousel
          resources={VIDEO_RESOURCES}
          currentIndex={currentVideoNumber}
          enabled={canLoadHeroVideo && isInViewport}
          onEnded={handleVideoEnded}
        />
      </div>

      {/* 视频指示器 */}
      <div className="absolute right-3 flex flex-col items-center gap-3 md:hidden">
        {VIDEO_RESOURCES.map((resource, index) => {
          const { poster, mobilePoster } = resource;
          const altText = t(`home3.heroSection.prompts.${resource.name}`);
          return (
            <picture key={`indicator_poster_${poster}`} onPointerEnter={() => handleVideoSelect(index)}>
              {mobilePoster ? <source media={`(max-width: ${BREAKPOINTS.SM_MAX}px)`} srcSet={mobilePoster} /> : null}
              <Image
                src={poster}
                alt={altText || 'AI generated video showcase'}
                width={40}
                height={40}
                loading="lazy"
                className={cn(
                  'h-5 w-5 cursor-pointer rounded-8 object-cover object-center opacity-30',
                  currentVideoNumber === index && 'h-10 w-10 opacity-100 transition-all duration-300 ease-in-out',
                  Math.abs(currentVideoNumber - index) === 1 && 'h-7 w-7 opacity-60',
                )}
              />
            </picture>
          );
        })}
      </div>

      <div className="absolute bottom-0 flex flex-col items-center">
        <h1
          className="mb-[34px] text-center font-semibold text-7xl text-white leading-tight sm:w-[345px] sm:text-[32px] md:mb-[17px] md:text-[38px] lg:text-[50px] xl:text-[80px]"
          style={{
            textShadow: '0px 2.25px 2.25px rgba(0, 0, 0, 0.25)',
          }}
        >
          {t('home3.heroSection.slogan')}
        </h1>
        <div className="mb-[30px] text-center text-[24px] leading-[34px] md:mb-[24px] md:text-[16px] md:leading-[22px]">
          {locale !== 'zh' && t('home3.heroSection.subSlogan')}
        </div>
        <div className="mb-[160px] h-[60px] w-[856px] md:hidden lg:w-[580px]">
          <PlaceholdersAndVanishInput
            placeholder={t(`home3.heroSection.prompts.${VIDEO_RESOURCES[currentVideoNumber].name}`)}
            bgImg={VIDEO_RESOURCES[currentVideoNumber].poster}
            buttonText={
              VIDEO_RESOURCES[currentVideoNumber].href
                ? t('home3.heroSection.experienceButton')
                : t('home3.heroSection.createButton')
            }
            tagText={VIDEO_RESOURCES[currentVideoNumber].href ? t('home3.heroSection.s1Tag') : undefined}
            hideImage={Boolean(VIDEO_RESOURCES[currentVideoNumber].href)}
            onSubmit={handleClickTryVidu}
          />
        </div>
        <div className="hidden items-center justify-center sm:flex-col sm:gap-4 md:mb-16 md:flex">
          <ClientLink
            href="/home"
            className="box-border inline-flex h-12 w-[168px] cursor-pointer items-center justify-center rounded-full border-[#rgba(0,0,0,0.3)] bg-system-black font-semibold text-base text-white hover:bg-[linear-gradient(180deg,rgba(0,0,0,0)0%,rgba(0,0,0,0.3)100%)]"
            onClick={() => {
              reportEvent('element_click', {
                element_name: 'landing - trynow',
                case_name: VIDEO_RESOURCES[currentVideoNumber].name,
                case_index: currentVideoNumber,
              });
            }}
          >
            {t('home3.heroSection.navHome')}
          </ClientLink>
        </div>
      </div>
    </div>
  );
};
